/*
  Shared Mapbox click-tooltip helper.

  Both sections/modern-mapbox.liquid and sections/modern-delivery-zones.liquid
  render pins from layers that live in the Mapbox Studio style. This attaches a
  click popup that reads feature properties at runtime, so pin styling stays
  owned by Studio and never needs a code change here.

  Usage:
    var handle = window.NovoMapPopup.attach(map, { layers: ["novo-locations-circles"] });
    handle.close();   // dismiss without unbinding (e.g. on tab change)
    handle.detach();  // unbind everything (section teardown)

  Nothing is added to the style — only listeners. Layer ids the style does not
  carry are skipped, so a renamed layer degrades to "no tooltip" instead of the
  gl-js "layer does not exist" error.
*/
(function () {
  "use strict";

  // A single click can return the same feature from several adjacent tiles,
  // and stacked centroids mean several genuinely different suburbs share a
  // point — so dedupe by identity, don't just take features[0].
  var MAX_FEATURES = 8;

  function noop() {}

  function escHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  // Tileset booleans survive as real booleans, but a value typed as text in
  // Studio arrives as the string "true" — treat both as set.
  function isTrue(value) {
    return value === true || value === "true" || value === 1 || value === "1";
  }

  function toList(value) {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      return value.split(",").map(function (s) {
        return s.trim();
      });
    }
    return value ? [value] : [];
  }

  function presentLayers(map, ids) {
    return ids.filter(function (id) {
      return !!map.getLayer(id);
    });
  }

  function featureKey(feature) {
    var props = feature.properties || {};
    if (props.shopify_id != null) return "id:" + props.shopify_id;
    if (feature.id != null) return "fid:" + feature.id;
    var geom = feature.geometry;
    var at = geom && Array.isArray(geom.coordinates) ? geom.coordinates.join(",") : "";
    return "lbl:" + props.label + "@" + at;
  }

  function dedupe(features) {
    var seen = {};
    return features.filter(function (f) {
      var key = featureKey(f);
      if (seen[key]) return false;
      seen[key] = true;
      return true;
    });
  }

  // Point features anchor to their own coordinates; anything else (a polygon
  // fill, say) anchors where the visitor actually clicked.
  function anchorFor(feature, lngLat) {
    var geom = feature.geometry;
    if (!geom || geom.type !== "Point" || !Array.isArray(geom.coordinates)) return lngLat;
    var coords = geom.coordinates.slice();
    // Keep the popup on the copy of the world that was clicked.
    while (Math.abs(lngLat.lng - coords[0]) > 180) {
      coords[0] += lngLat.lng > coords[0] ? 360 : -360;
    }
    return coords;
  }

  function renderFeature(feature) {
    var p = feature.properties || {};
    var rows = "";

    if (p.label) {
      rows += '<strong class="novo-map-popup__title">' + escHtml(p.label) + "</strong>";
    }
    if (p.provider_all) {
      rows +=
        '<span class="novo-map-popup__provider">' +
        escHtml(p.provider_all) +
        (isTrue(p.multi_provider) ? ' <em class="novo-map-popup__shared">(shared)</em>' : "") +
        "</span>";
    }
    if (p.region_all) {
      rows += '<small class="novo-map-popup__region">' + escHtml(p.region_all) + "</small>";
    }

    return rows ? '<div class="novo-map-popup__item">' + rows + "</div>" : "";
  }

  function buildHtml(features) {
    var shown = features.slice(0, MAX_FEATURES);
    var html = shown
      .map(renderFeature)
      .filter(Boolean)
      .join("");
    if (!html) return "";
    var extra = features.length - shown.length;
    if (extra > 0) {
      html += '<small class="novo-map-popup__more">+' + extra + " more here</small>";
    }
    return html;
  }

  function attach(map, options) {
    var opts = options || {};
    var layerIds = toList(opts.layers).filter(Boolean).map(String);
    if (!map || !window.mapboxgl || !layerIds.length) {
      return { close: noop, detach: noop };
    }

    // closeOnClick is deliberately off: gl-js registers its own map click
    // handler when the popup is added, which would fire after ours and close
    // a popup we just opened for a second feature. Closing is handled below.
    var popup = new window.mapboxgl.Popup({
      closeButton: true,
      closeOnClick: false,
      maxWidth: opts.maxWidth || "280px",
      className: "novo-map-popup" + (opts.className ? " " + opts.className : ""),
    });

    var bound = presentLayers(map, layerIds);

    function onClick(e) {
      // Re-resolved per click so layers added to the style after attach
      // (or a style reload) are picked up.
      var layers = presentLayers(map, layerIds);
      if (!layers.length) return;

      var hits = dedupe(map.queryRenderedFeatures(e.point, { layers: layers }));
      var html = buildHtml(hits);
      if (!html) {
        popup.remove();
        return;
      }
      popup.setLngLat(anchorFor(hits[0], e.lngLat)).setHTML(html).addTo(map);
    }

    function onEnter() {
      map.getCanvas().style.cursor = "pointer";
    }

    function onLeave() {
      map.getCanvas().style.cursor = "";
    }

    map.on("click", onClick);
    bound.forEach(function (id) {
      map.on("mouseenter", id, onEnter);
      map.on("mouseleave", id, onLeave);
    });

    return {
      close: function () {
        popup.remove();
      },
      detach: function () {
        popup.remove();
        map.off("click", onClick);
        bound.forEach(function (id) {
          map.off("mouseenter", id, onEnter);
          map.off("mouseleave", id, onLeave);
        });
      },
    };
  }

  window.NovoMapPopup = { attach: attach };
})();
